import React, { useEffect, useRef, useState, useCallback } from 'react';
import FileService from '../services/FileService';
import Select from 'react-select';

// Available fields for dynamic insertion
const AVAILABLE_FIELDS = [
  { label: 'Name', value: 'name' },
  { label: 'Phone Number', value: 'phone_number' },
  ...Array.from({ length: 10 }, (_, i) => ({
    label: `Info ${i + 1}`,
    value: `info${i + 1}`
  }))
];

// TextAreaWithFields component for handling dynamic field insertion
const TextAreaWithFields = ({ value, onChange, placeholder, rows = 4 }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  const containerRef = useRef(null);

  // Handle text change
  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setCursorPosition(e.target.selectionStart);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === '@') {
      setDropdownOpen(true);
    }
  };

  // Handle field selection
  const handleFieldSelect = (fieldValue) => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    // Get text before and after cursor
    const textBefore = value.substring(0, start - 1); // Remove the @ character
    const textAfter = value.substring(end);

    // Insert field at cursor position with $ prefix
    const newValue = `${textBefore}$${fieldValue} ${textAfter}`;
    onChange(newValue);

    // Calculate new cursor position
    const newPosition = start + fieldValue.length + 1; // +1 for the space
    setCursorPosition(newPosition);

    // Close dropdown
    setDropdownOpen(false);

    // Focus back on textarea and set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  // Use raw value in textarea to preserve $ prefix
  const getDisplayValue = () => {
    return value || '';
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update cursor position when value changes externally
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [value, cursorPosition]);

  // Style for highlighting fields in the text
  const getHighlightedText = () => {
    if (!value) return '';

    const fieldRegex = /\$(\b(name|phone_number|info\d+)\b)/g;
    let lastIndex = 0;
    const parts = [];
    let match;

    while ((match = fieldRegex.exec(value)) !== null) {
      // Add text before the field
      if (match.index > lastIndex) {
        parts.push(value.substring(lastIndex, match.index));
      }

      // Add the field with highlighting
      parts.push(`<span class="field-highlight">${match[1]}</span>`);
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < value.length) {
      parts.push(value.substring(lastIndex));
    }

    return parts.join('');
  };

  return (
    <div className="textarea-with-fields" ref={containerRef}>
      <div className="field-hint">Press @ to insert a field</div>
      <div className="textarea-container">
        <textarea
          ref={textareaRef}
          value={getDisplayValue()} // Use display value without $ prefix
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          rows={rows}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            lineHeight: '1.5',
            direction: 'ltr',
            resize: 'vertical',
          }}
        />
        {dropdownOpen && (
          <div
            className="fields-dropdown"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              background: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 1000,
              width: '200px',
              marginTop: '4px',
            }}
          >
            {AVAILABLE_FIELDS.map(field => (
              <div
                key={field.value}
                className="field-option"
                onClick={() => handleFieldSelect(field.value)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s',
                }}
              >
                <span style={{ fontWeight: 500, color: '#333' }}>{field.label}</span>
                <span style={{ color: '#666', fontSize: '12px', fontFamily: 'monospace' }}>
                  ${field.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <p style={{ fontSize: '12px', color: '#666', marginTop: '4px', marginBottom: 0 }}>
        You can use variables like <strong>$info1</strong> to <strong>$info5</strong> in your message.
      </p>
      <style>
        {`
          .textarea-container {
            position: relative;
          }
          .field-hint {
            font-size: 12px;
            color: #666;
            margin-bottom: 4px;
          }
          .field-highlight {
            background-color: #f3e5f5;
            color: #4a148c;
            font-weight: bold;
            padding: 2px 4px;
            border-radius: 2px;
            margin: 0 2px;
          }
          .field-option:hover {
            background-color: #f3e5f5;
          }
        `}
      </style>
    </div>
  );
};

const PropertyPanel = ({
  selectedNode,
  onUpdateNodeData,
  rasaIntents,
  onOpenIntentManager,
  allNodes,
  onDeleteNode,
  rasaSlots,
  userGroup
}) => {
  useEffect(() => {
    if (selectedNode && !selectedNode.data.nodeId) {
      const updatedData = { ...selectedNode.data, nodeId: selectedNode.id };
      onUpdateNodeData(selectedNode.id, updatedData);
    }
  }, [selectedNode, onUpdateNodeData]);

  if (!selectedNode) {
    return (
      <div className="property-panel">
        <h3>Node Properties</h3>
        <p>Select a node to edit its properties</p>
      </div>
    );
  }

  const { id, data, type } = selectedNode;
  const nodeType = type.replace('Node', '');

  // Update node data for any field
  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    onUpdateNodeData(id, { ...data, [name]: inputType === 'checkbox' ? checked : value });
  };

  // Update node label (name) specifically
  const handleLabelChange = (e) => {
    onUpdateNodeData(id, { ...data, label: e.target.value });
  };

  // Update message text specifically
  const handleTextChange = (newText) => {
    onUpdateNodeData(id, { ...data, text: newText });
  };

  // Update question text specifically
  const handleQuestionTextChange = (newText) => {
    onUpdateNodeData(id, { ...data, questionText: newText });
  };

  // Create list of available nodes for "next" dropdown
  const availableNodes = allNodes
    .filter(node => node.id !== id) // Don't include self
    .map(node => ({
      id: node.id,
      label: node.data.label || node.id
    }));

  // For decision node: get all available intents not already used in conditions
  const getUnusedIntents = (currentConditions) => {
    const usedIntents = (currentConditions || []).map(c => c.condition);
    return rasaIntents.filter(intent => !usedIntents.includes(intent.intent || intent.name));
  };

  // For decision node: get all available slots
  const getAvailableSlots = () => {
    return rasaSlots || [];
  };

  // Handle slot selection for captureResponse
  const handleSlotChange = (selectedSlots) => {
    if (!selectedNode) return;

    // Get slot names from selected options
    const slotTypes = selectedSlots.map(slot => slot.value);

    // Always pull latest questions and type from rasaSlots
    const slotsData = {};
    slotTypes.forEach(slotType => {
      const slot = rasaSlots.find(s => (s.slot || s.name) === slotType);
      if (slot) {
        slotsData[slotType] = {
          questions: slot.questions || [],
          type: slot.type || 'text'
        };
      }
    });

    // Update node data with both slotTypes and slots (always set slots)
    const updatedData = {
      ...selectedNode.data,
      slotTypes,
      slots: slotsData, // always set slots
      type: selectedNode.data.isAudio ? 'streamCaptureResponse' : 'captureResponse',
      nodeType: 'decision'  // Ensure nodeType is set
    };

    // Ensure next node is set for capture response
    if (!updatedData.next && availableNodes.length > 0) {
      updatedData.next = availableNodes[0].id;
    }

    onUpdateNodeData(selectedNode.id, updatedData);
  };

  // Handle audio file upload
  const handleAudioUpload = async (file, nodeType) => {
    try {
      const result = await FileService.uploadAudio(file);

      if (nodeType === 'message') {
        onUpdateNodeData(selectedNode.id, {
          ...selectedNode.data,
          text: result.path,
          isAudio: true,
          type: 'streamFile'
        });
      } else if (nodeType === 'decision') {
        const type = selectedNode.data.learnResponse ? 'streamLearnResponse' :
          selectedNode.data.captureResponse ? 'streamCaptureResponse' :
            'streamFile';
        onUpdateNodeData(selectedNode.id, {
          ...selectedNode.data,
          questionText: result.path,
          isAudio: true,
          type
        });
      }
    } catch (error) {
      console.error('Error uploading audio:', error);
      alert(`Failed to upload audio: ${error.message}`);
    }
  };

  // Update audio/text selection for message node
  const handleMessageTypeChange = (isAudio) => {
    onUpdateNodeData(id, {
      ...data,
      isAudio,
      type: isAudio ? 'streamFile' : 'message'
    });
  };

  // Update audio/text selection for decision node
  const handleDecisionTypeChange = (isAudio) => {
    const newType = isAudio ?
      (data.learnResponse ? 'streamLearnResponse' :
        data.captureResponse ? 'streamCaptureResponse' : 'streamFile') :
      (data.learnResponse ? 'learnResponse' :
        data.captureResponse ? 'captureResponse' : 'message');

    onUpdateNodeData(id, {
      ...data,
      isAudio,
      type: newType
    });
  };

  const renderSlotSelection = () => {
    if (!selectedNode || selectedNode.data.nodeType !== 'decision' || !selectedNode.data.captureResponse) {
      return null;
    }

    const options = rasaSlots.map(slot => ({
      value: slot.slot || slot.name,
      label: slot.slot || slot.name
    }));

    const value = (selectedNode.data.slotTypes || []).map(slotType => ({
      value: slotType,
      label: slotType
    }));

    return (
      <div className="property-group">
        <div className="form-group">
          <label>Required Slots:</label>
          <Select
            isMulti
            value={value}
            options={options}
            onChange={handleSlotChange}
            placeholder="Select slots..."
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '38px',
                background: '#fff',
                borderColor: '#ddd',
                '&:hover': {
                  borderColor: '#9c27b0'
                }
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#f3e5f5',
                borderRadius: '4px'
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: '#4a148c',
                fontWeight: 500
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: '#4a148c',
                '&:hover': {
                  backgroundColor: '#e1bee7',
                  color: '#4a148c'
                }
              })
            }}
          />
        </div>

        {value.length > 0 && (
          <div className="slot-questions">
            <h4>Slot Questions</h4>
            {value.map(slotOption => {
              // Find the full slot object from the rasaSlots prop
              const fullSlot = rasaSlots.find(s => (s.slot || s.name) === slotOption.value);
              const questions = fullSlot ? fullSlot.questions : [];

              return (
                <div key={slotOption.value} className="slot-question-group">
                  <h5>{slotOption.label}</h5>
                  {questions && questions.length > 0 ? (
                    <ul>
                      {questions.map((question, index) => (
                        <li key={index}>{question}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-questions">No questions defined for this slot. Please add them in the RASA Manager.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderNodeProperties = () => {
    // RBAC: If user is 'user', they can only edit Message Text or Audio.
    // They cannot change Start Node, Next Node, Conditions, etc. (handled by hiding those fields)
    const isUserRole = (userGroup || 'user').toLowerCase() === 'user';

    switch (nodeType) {
      case 'message':
        return (
          <>
            <div className="form-group">
              <label>Input Type</label>
              <div className="radio-group" style={{ marginBottom: '0.75rem' }}>
                <label>
                  <input
                    type="radio"
                    name="inputType"
                    value="text"
                    checked={!data.isAudio}
                    onChange={() => handleMessageTypeChange(false)}
                  />
                  Text
                </label>
                <label>
                  <input
                    type="radio"
                    name="inputType"
                    value="audio"
                    checked={data.isAudio}
                    onChange={() => handleMessageTypeChange(true)}
                  />
                  Audio
                </label>
              </div>

              {!data.isAudio ? (
                <>
                  <label htmlFor="text">Message Text</label>
                  <TextAreaWithFields
                    value={data.text || ''}
                    onChange={handleTextChange}
                    placeholder="Enter message text"
                    rows={4}
                  />
                </>
              ) : (
                <>
                  <label htmlFor="audioFile">Audio File</label>
                  <div className="audio-upload">
                    <input
                      type="file"
                      id="audioFile"
                      accept="audio/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleAudioUpload(file, 'message');
                        }
                      }}
                      className="form-control"
                    />
                    {data.text && data.isAudio && (
                      <div className="audio-preview">
                        <span>Current audio: {data.text.split('/').pop()}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Hide advanced settings for User Role */}
            {!isUserRole && (
              <>
                <div className="form-group">
                  <label htmlFor="language">Language</label>
                  <select
                    id="language"
                    name="language"
                    value={data.language || 'eng'}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    <option value="eng">English</option>
                    <option value="hin">Hindi</option>
                    <option value="tam">Tamil</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="next">Next Node</label>
                  <select
                    id="next"
                    name="next"
                    value={data.next || ''}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    <option value="">Select Next Node</option>
                    {availableNodes.map(node => (
                      <option key={node.id} value={node.id}>
                        {node.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </>
        );
      case 'decision':
        return (
          <>
            {/* 1 & 2. Input Type and Message */}
            <div className="form-group">
              <label>Input Type</label>
              <div className="radio-group" style={{ marginBottom: '0.75rem' }}>
                <label>
                  <input
                    type="radio"
                    name="inputType"
                    value="text"
                    checked={!data.isAudio}
                    onChange={() => handleDecisionTypeChange(false)}
                  />
                  Text
                </label>
                <label>
                  <input
                    type="radio"
                    name="inputType"
                    value="audio"
                    checked={data.isAudio}
                    onChange={() => handleDecisionTypeChange(true)}
                  />
                  Audio
                </label>
              </div>

              <label>First Message/Question</label>
              {!data.isAudio ? (
                <TextAreaWithFields
                  value={data.questionText || ''}
                  onChange={handleQuestionTextChange}
                  placeholder="Question to ask the customer"
                  rows={3}
                />
              ) : (
                <div className="audio-upload">
                  <input
                    type="file"
                    id="audioFile"
                    accept="audio/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleAudioUpload(file, 'decision');
                      }
                    }}
                    className="form-control"
                  />
                  {data.questionText && data.isAudio && (
                    <div className="audio-preview">
                      <span>Current audio: {data.questionText.split('/').pop()}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Hide advanced settings for User Role */}
            {!isUserRole && (
              <>
                {/* 3. Learn Response */}
                <div className="form-group">
                  <div className="checkbox-group">
                    <label style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        name="learnResponse"
                        checked={data.learnResponse || false}
                        onChange={e => {
                          const isChecked = e.target.checked;
                          const newData = {
                            ...data,
                            learnResponse: isChecked,
                            captureResponse: isChecked ? false : data.captureResponse,
                            conditions: isChecked ? (data.conditions || []) : undefined,
                            slots: isChecked ? undefined : data.slots
                          };
                          if (data.isAudio) {
                            newData.type = isChecked ? 'streamLearnResponse' : 'streamFile';
                          }
                          onUpdateNodeData(id, newData);
                        }}
                        style={{ marginRight: 6 }}
                      />
                      Learn Response
                    </label>
                  </div>
                  {/* Related options for Learn Response */}
                  {data.learnResponse && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Conditions</label>
                      <div className="conditions-list">
                        {(data.conditions || []).map((condition, index) => (
                          <div key={index} className="condition-item">
                            <div className="form-group">
                              <label>Intent</label>
                              <select
                                className="form-control"
                                value={condition.condition || ''}
                                onChange={(e) => {
                                  const newConditions = [...data.conditions];
                                  newConditions[index].condition = e.target.value;
                                  onUpdateNodeData(id, { ...data, conditions: newConditions });
                                }}
                              >
                                <option value="">Select Intent</option>
                                {rasaIntents.map(intent => (
                                  <option key={intent.intent || intent.name} value={intent.intent || intent.name} disabled={data.conditions.some((c, i) => c.condition === (intent.intent || intent.name) && i !== index)}>
                                    {intent.intent || intent.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Next Node</label>
                              <select
                                className="form-control"
                                value={condition.next || ''}
                                onChange={(e) => {
                                  const newConditions = [...data.conditions];
                                  newConditions[index].next = e.target.value;
                                  onUpdateNodeData(id, { ...data, conditions: newConditions });
                                }}
                              >
                                <option value="">Select Next Node</option>
                                {availableNodes.map(node => (
                                  <option key={node.id} value={node.id}>
                                    {node.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => {
                                const newConditions = [...data.conditions];
                                newConditions.splice(index, 1);
                                onUpdateNodeData(id, { ...data, conditions: newConditions });
                              }}
                              style={{ marginTop: '5px' }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          const conditions = data.conditions || [];
                          conditions.push({ condition: '', next: '' });
                          onUpdateNodeData(id, { ...data, conditions });
                        }}
                        style={{ marginTop: '10px', marginBottom: '15px' }}
                      >
                        Add Condition
                      </button>
                      {/* Use FAQ as default for Learn Response */}
                      <div className="checkbox-group" style={{ marginTop: '0.5rem' }}>
                        <label>
                          <input
                            type="checkbox"
                            id="useFaq"
                            name="useFaq"
                            checked={data.useFaq || false}
                            onChange={handleInputChange}
                          />
                          Use FAQ node as default
                        </label>
                      </div>
                      {/* Show Default FAQ Node if checked */}
                      {data.useFaq && (
                        <div className="form-group">
                          <label>Default (FAQ) Next Node</label>
                          <div className="form-control" style={{ background: '#f3e5f5' }}>
                            {(availableNodes.find(n => n.id === data.useFaq) ? availableNodes.find(n => n.id === data.useFaq).label : 'FAQ Node')}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 4. Capture Response */}
                <div className="form-group">
                  <div className="checkbox-group">
                    <label style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        name="captureResponse"
                        checked={data.captureResponse || false}
                        onChange={e => {
                          const isChecked = e.target.checked;
                          const newData = {
                            ...data,
                            captureResponse: isChecked,
                            learnResponse: isChecked ? false : data.learnResponse,
                            slots: isChecked ? [] : undefined,
                            conditions: isChecked ? undefined : data.conditions
                          };
                          if (data.isAudio) {
                            newData.type = isChecked ? 'streamCaptureResponse' : 'streamFile';
                          }
                          onUpdateNodeData(id, newData);
                        }}
                        style={{ marginRight: 6 }}
                      />
                      Capture Response
                    </label>
                  </div>
                  {/* Related options for Capture Response */}
                  {data.captureResponse && renderSlotSelection()}
                </div>

                {/* Other node properties (language, next, etc.) */}
                <div className="form-group">
                  <label htmlFor="language">Language</label>
                  <select
                    id="language"
                    name="language"
                    value={data.language || 'eng'}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    <option value="eng">English</option>
                    <option value="hin">Hindi</option>
                    <option value="tam">Tamil</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="next">Next Node</label>
                  <select
                    id="next"
                    name="next"
                    value={data.next || ''}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    <option value="">Select Next Node</option>
                    {availableNodes.map(node => (
                      <option key={node.id} value={node.id}>
                        {node.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 5. Save Response at the end */}
                <div className="form-group">
                  <div className="checkbox-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <label style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        name="saveResponse"
                        checked={data.saveResponse || false}
                        onChange={e => {
                          const isChecked = e.target.checked;
                          const newData = {
                            ...data,
                            saveResponse: isChecked
                          };
                          // Reset mappings if unchecked
                          if (!isChecked) {
                            newData.saveResponseMappings = [];
                          } else if (!Array.isArray(data.saveResponseMappings)) {
                            newData.saveResponseMappings = [];
                          }
                          onUpdateNodeData(id, newData);
                        }}
                        style={{ marginRight: 6 }}
                      />
                      Save Response
                    </label>
                    {data.saveResponse && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {(data.saveResponseMappings || []).map((mapping, idx) => {
                          // Build dropdown options
                          const options = [
                            { label: 'User Response', value: 'User Response' },
                            { label: 'Learnt Response', value: 'Learnt Response' },
                          ];
                          if (data.captureResponse && Array.isArray(data.slotTypes)) {
                            data.slotTypes.forEach(slotName => {
                              options.push({ label: `Slot: ${slotName}`, value: `slot:${slotName}` });
                            });
                          }
                          return (
                            <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                              <select
                                className="form-control"
                                style={{ width: 170, minWidth: 120 }}
                                value={mapping.responseType || ''}
                                onChange={e => {
                                  const newMappings = [...(data.saveResponseMappings || [])];
                                  newMappings[idx].responseType = e.target.value || null;
                                  onUpdateNodeData(id, { ...data, saveResponseMappings: newMappings });
                                }}
                              >
                                <option value="">Select Response</option>
                                {options.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                              <select
                                className="form-control"
                                style={{ width: 120, minWidth: 80 }}
                                value={mapping.infoField || ''}
                                onChange={e => {
                                  const newMappings = [...(data.saveResponseMappings || [])];
                                  // Prevent duplicate info fields
                                  const val = e.target.value || null;
                                  if (val && newMappings.some((m, i) => m.infoField === val && i !== idx)) {
                                    alert('This info field is already mapped.');
                                    return;
                                  }
                                  newMappings[idx].infoField = val;
                                  onUpdateNodeData(id, { ...data, saveResponseMappings: newMappings });
                                }}
                              >
                                <option value="">Select Field</option>
                                {Array.from({ length: 45 }, (_, i) => `info${i + 6}`).map(field => (
                                  <option key={field} value={field}>{field}</option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => {
                                  const newMappings = [...(data.saveResponseMappings || [])];
                                  newMappings.splice(idx, 1);
                                  onUpdateNodeData(id, { ...data, saveResponseMappings: newMappings });
                                }}
                                style={{ padding: '2px 8px', fontSize: 18, marginLeft: 4 }}
                                title="Remove Mapping"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            const newMappings = [...(data.saveResponseMappings || [])];
                            newMappings.push({ responseType: null, infoField: null });
                            onUpdateNodeData(id, { ...data, saveResponseMappings: newMappings });
                          }}
                          style={{ marginTop: 4, alignSelf: 'flex-start' }}
                        >
                          Add Mapping
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        );
      case 'api':
        return (
          <>
            <div className="form-group">
              <label htmlFor="apiEndpoint">API Endpoint</label>
              <input
                type="text"
                id="apiEndpoint"
                name="apiEndpoint"
                value={data.apiEndpoint || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="API URL"
              />
            </div>
            <div className="form-group">
              <label htmlFor="payload">Payload (JSON)</label>
              <textarea
                id="payload"
                name="payload"
                value={data.payload ? JSON.stringify(data.payload, null, 2) : '{}'}
                onChange={(e) => {
                  try {
                    const payload = JSON.parse(e.target.value);
                    onUpdateNodeData(id, { ...data, payload });
                  } catch (error) {
                    // Handle invalid JSON
                  }
                }}
                className="form-control"
                rows={6}
              />
            </div>
            <div className="form-group">
              <label htmlFor="next">Next Node</label>
              <select
                id="next"
                name="next"
                value={data.next || ''}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Select Next Node</option>
                {availableNodes.map(node => (
                  <option key={node.id} value={node.id}>
                    {node.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        );
      case 'faq':
        return (
          <>
            <div className="form-group">
              {/* Language Selection */}
              <label htmlFor="language">Language</label>
              <select
                id="language"
                name="language"
                value={data.language || 'eng'}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="eng">English</option>
                <option value="hin">Hindi</option>
                <option value="tam">Tamil</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="next">Next Node</label>
              <select
                id="next"
                name="next"
                value={data.next || ''}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Select Next Node</option>
                {availableNodes.map(node => (
                  <option key={node.id} value={node.id}>
                    {node.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        );
      default:
        return <p>Unknown node type: {nodeType}</p>;
    }
  };

  return (
    <div className="property-panel">
      {selectedNode ? (
        <>
          <input
            type="text"
            id="label"
            name="label"
            value={data.label || ''}
            onChange={handleLabelChange}
            className="property-panel-label-input"
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'var(--primary)',
              marginBottom: '1.5rem',
              border: 'none',
              background: 'transparent',
              outline: 'none',
              width: '100%'
            }}
            placeholder="Enter node name"
          />
          {renderNodeProperties()}
          <button className="btn btn-danger" onClick={onDeleteNode}>Delete Node</button>
        </>
      ) : (
        <p>Select a node to edit its properties</p>
      )}
    </div>
  );
};

// Add styles at the end of the file
const styles = `
  .property-panel {
    width: 350px;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem; /* Let gap control spacing */
  }

  .form-group {
    margin-bottom: 0; /* Remove margin, rely on parent gap */
  }

  .form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
  }

  .form-control {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
  }

  .slot-questions {
    margin-top: 1.5rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 1rem;
    background: #fafafa;
  }

  .slot-questions h4 {
    margin-bottom: 1rem;
    color: #333;
    font-size: 1rem;
  }

  .slot-question-group {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: white;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .slot-question-group h5 {
    color: var(--primary);
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
  }

  .slot-question-group ul {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 200px;
    overflow-y: auto;
  }

  .slot-question-group li {
    padding: 0.5rem;
    margin-bottom: 0.5rem;
    background: #f5f5f5;
    border-radius: 4px;
    font-size: 0.9rem;
  }

  .radio-group {
    display: flex;
    gap: 20px;
    margin-bottom: 15px;
  }

  .radio-group label {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    margin-bottom: 0;
  }

  .checkbox-group {
    margin: 0.5rem 0; /* Reduced margin */
  }

  .checkbox-group label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    margin-bottom: 0;
  }

  .property-panel-label-input {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--primary);
    margin-bottom: 1.5rem;
    border: none;
    background: transparent;
    outline: none;
    width: 100%;
  }

  .btn-danger {
    width: 100%;
    padding: 0.75rem;
    margin-top: 1rem;
    background: #d32f2f;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-danger:hover {
    background: #b71c1c;
  }
`;

// Add style tag to document (move outside component to avoid re-running on every render)
if (!document.querySelector('#property-panel-styles')) {
  const styleSheet = document.createElement("style");
  styleSheet.id = 'property-panel-styles';
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default PropertyPanel;
